import {
  DeleteOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  ProTable,
  type ActionType,
  type ProColumns,
  type ProFormInstance,
} from '@ant-design/pro-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type MaterialType =
  | 'PARENTAL'
  | 'TRANSIENT_POOL'
  | 'STABLE_POOL'
  | 'CLONE'
  | 'ENGINEERED_LINE'
  | 'MIXED_POPULATION'
  | 'OTHER';
type ModificationType =
  | 'KI'
  | 'TAG'
  | 'KO'
  | 'OE'
  | 'KD'
  | 'REPORTER'
  | 'OTHER';
type TagPosition = 'N_TERMINAL' | 'C_TERMINAL' | 'INTERNAL' | 'UNSPECIFIED';
type ModificationStrategy =
  | 'ENDOGENOUS_KI'
  | 'EXOGENOUS_OE'
  | 'STABLE_INTEGRATION'
  | 'TRANSIENT_EXPRESSION'
  | 'OTHER';
type ValidationStatus = 'UNVERIFIED' | 'SCREENING' | 'VERIFIED';
type MaterialStatus = 'ACTIVE' | 'INACTIVE';

type CellLine = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
};

type CellMaterial = {
  id: string;
  cellLineId: string;
  cellLineCode: string;
  parentMaterialId: string | null;
  type: MaterialType;
  name: string;
  modificationType: ModificationType | null;
  targetGene: string | null;
  tagName: string | null;
  tagPosition: TagPosition | null;
  modificationStrategy: ModificationStrategy | null;
  constructName: string | null;
  selectionMarker: string | null;
  cloneNo: string | null;
  validationStatus: ValidationStatus;
  notes: string | null;
  status: MaterialStatus;
  version: number;
};

type CellMaterialForm = {
  cellLineId: string;
  parentMaterialId?: string;
  type: MaterialType;
  name: string;
  modificationType?: ModificationType;
  targetGene?: string;
  tagName?: string;
  tagPosition?: TagPosition;
  modificationStrategy?: ModificationStrategy;
  constructName?: string;
  selectionMarker?: string;
  cloneNo?: string;
  validationStatus: ValidationStatus;
  notes?: string;
};

const materialTypes: MaterialType[] = [
  'PARENTAL',
  'TRANSIENT_POOL',
  'STABLE_POOL',
  'CLONE',
  'ENGINEERED_LINE',
  'MIXED_POPULATION',
  'OTHER',
];
const modificationTypes: ModificationType[] = [
  'KI',
  'TAG',
  'KO',
  'OE',
  'KD',
  'REPORTER',
  'OTHER',
];
const tagPositions: TagPosition[] = [
  'N_TERMINAL',
  'C_TERMINAL',
  'INTERNAL',
  'UNSPECIFIED',
];
const modificationStrategies: ModificationStrategy[] = [
  'ENDOGENOUS_KI',
  'EXOGENOUS_OE',
  'STABLE_INTEGRATION',
  'TRANSIENT_EXPRESSION',
  'OTHER',
];
const validationStatuses: ValidationStatus[] = [
  'UNVERIFIED',
  'SCREENING',
  'VERIFIED',
];

export function MaterialsPage() {
  const { t, i18n } = useTranslation();
  const [lines, setLines] = useState<CellLine[]>([]);
  const [allMaterials, setAllMaterials] = useState<CellMaterial[]>([]);
  const [editing, setEditing] = useState<CellMaterial>();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<CellMaterialForm>();
  const [messageApi, holder] = message.useMessage();
  const [busyId, setBusyId] = useState<string>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const actionRef = useRef<ActionType | undefined>(undefined);
  const formRef = useRef<ProFormInstance | undefined>(undefined);
  const selectedLineId = Form.useWatch('cellLineId', form);
  const selectedModificationType = Form.useWatch('modificationType', form);

  useEffect(() => {
    void Promise.all([
      fetch('/api/v1/cell-lines?limit=200', { credentials: 'include' }),
      fetch('/api/v1/cell-materials?status=ALL&limit=200', {
        credentials: 'include',
      }),
    ])
      .then(async ([lineResponse, materialResponse]) => {
        if (!lineResponse.ok || !materialResponse.ok) throw new Error();
        const lineData = (await lineResponse.json()) as {
          items: CellLine[];
        };
        const materialData = (await materialResponse.json()) as {
          items: CellMaterial[];
        };
        setLines(lineData.items);
        setAllMaterials(materialData.items);
      })
      .catch(() => void messageApi.error(t('materials.loadFailed')));
  }, [messageApi, t]);

  const lineName = (line: CellLine) =>
    i18n.resolvedLanguage === 'en-US'
      ? (line.nameEn ?? line.nameZh)
      : (line.nameZh ?? line.nameEn);
  const lineOptions = lines.map((line) => ({
    value: line.id,
    label:
      lineName(line) && lineName(line) !== line.code
        ? `${line.code} — ${lineName(line)}`
        : line.code,
  }));
  const optionItems = <T extends string>(
    values: readonly T[],
    prefix: string,
  ) => values.map((value) => ({ value, label: t(`${prefix}.${value}`) }));
  const materialTypeOptions = optionItems(
    materialTypes,
    'samples.materialTypeOptions',
  );
  const modificationTypeOptions = optionItems(
    modificationTypes,
    'samples.modificationTypeOptions',
  );
  const tagPositionOptions = optionItems(
    tagPositions,
    'samples.tagPositionOptions',
  );
  const modificationStrategyOptions = optionItems(
    modificationStrategies,
    'samples.modificationStrategyOptions',
  );
  const validationStatusOptions = optionItems(
    validationStatuses,
    'samples.validationStatusOptions',
  );
  const statusOptions = (['ALL', 'ACTIVE', 'INACTIVE'] as const).map(
    (value) => ({
      value,
      label:
        value === 'ALL'
          ? t('materials.allStatuses')
          : t(`samples.materialStatusOptions.${value}`),
    }),
  );
  const helpLabel = (label: string, help: string) => (
    <Space size={4} align="center">
      <span>{label}</span>
      <Tooltip title={help}>
        <QuestionCircleOutlined
          aria-label={help}
          style={{ color: '#8c8c8c' }}
        />
      </Tooltip>
    </Space>
  );
  const parentOptions = useMemo(
    () =>
      allMaterials
        .filter(
          (material) =>
            material.cellLineId === selectedLineId &&
            material.status === 'ACTIVE' &&
            material.id !== editing?.id,
        )
        .map((material) => ({
          value: material.id,
          label: `${material.name} · ${t(
            `samples.materialTypeOptions.${material.type}`,
          )}`,
        })),
    [allMaterials, editing?.id, selectedLineId, t],
  );

  const requestMaterials = async (params: Record<string, unknown>) => {
    const search = new URLSearchParams({ limit: '200' });
    for (const [key, value] of [
      ['query', params.query],
      ['cellLineId', params.cellLineId],
      ['status', params.status],
      ['type', params.type],
      ['validationStatus', params.validationStatus],
    ] as const) {
      if (typeof value === 'string' && value.trim() && value !== 'ALL')
        search.set(key, value.trim());
    }
    if (params.status === 'ALL') search.set('status', 'ALL');
    try {
      const response = await fetch(`/api/v1/cell-materials?${search}`, {
        credentials: 'include',
      });
      if (response.status === 401) {
        window.location.assign('/login');
        return { data: [], success: false, total: 0 };
      }
      if (!response.ok) throw new Error();
      const payload = (await response.json()) as { items: CellMaterial[] };
      return {
        data: payload.items,
        success: true,
        total: payload.items.length,
      };
    } catch {
      await messageApi.error(t('materials.loadFailed'));
      return { data: [], success: false, total: 0 };
    }
  };

  const openCreate = () => {
    setEditing(undefined);
    form.resetFields();
    form.setFieldsValue({
      type: 'TRANSIENT_POOL',
      validationStatus: 'UNVERIFIED',
    });
    setOpen(true);
  };
  const openEdit = (material: CellMaterial) => {
    setEditing(material);
    form.setFieldsValue({
      cellLineId: material.cellLineId,
      parentMaterialId: material.parentMaterialId ?? undefined,
      type: material.type,
      name: material.name,
      modificationType: material.modificationType ?? undefined,
      targetGene: material.targetGene ?? undefined,
      tagName: material.tagName ?? undefined,
      tagPosition: material.tagPosition ?? undefined,
      modificationStrategy: material.modificationStrategy ?? undefined,
      constructName: material.constructName ?? undefined,
      selectionMarker: material.selectionMarker ?? undefined,
      cloneNo: material.cloneNo ?? undefined,
      validationStatus: material.validationStatus,
      notes: material.notes ?? undefined,
    } as unknown as Partial<CellMaterialForm>);
    setOpen(true);
  };
  const save = async (values: CellMaterialForm) => {
    const response = await fetch(
      editing
        ? `/api/v1/cell-materials/${editing.id}`
        : '/api/v1/cell-materials',
      {
        method: editing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(
          editing ? { ...values, version: editing.version } : values,
        ),
      },
    );
    if (!response.ok) {
      await messageApi.error(t('samples.materialSaveFailed'));
      return;
    }
    const saved = (await response.json()) as CellMaterial;
    setAllMaterials((current) =>
      editing
        ? current.map((item) => (item.id === saved.id ? saved : item))
        : [saved, ...current],
    );
    setOpen(false);
    setEditing(undefined);
    form.resetFields();
    await actionRef.current?.reload();
    await messageApi.success(t('samples.materialSaved'));
  };
  const updateStatus = async (material: CellMaterial) => {
    setBusyId(material.id);
    try {
      const response = await fetch(
        `/api/v1/cell-materials/${material.id}/status`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            status: material.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
            version: material.version,
          }),
        },
      );
      if (!response.ok) throw new Error();
      await actionRef.current?.reload();
      await messageApi.success(t('samples.materialStatusSaved'));
    } catch {
      await messageApi.error(t('samples.materialStatusFailed'));
    } finally {
      setBusyId(undefined);
    }
  };
  const deleteMaterial = async (material: CellMaterial) => {
    setBusyId(material.id);
    try {
      const response = await fetch(`/api/v1/cell-materials/${material.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ version: material.version }),
      });
      if (!response.ok) {
        if (response.status === 409) {
          await messageApi.error(t('samples.materialDeleteReferenced'));
          return;
        }
        throw new Error();
      }
      await actionRef.current?.reload();
      await messageApi.success(t('samples.materialDeleted'));
    } catch {
      await messageApi.error(t('samples.materialDeleteFailed'));
    } finally {
      setBusyId(undefined);
    }
  };

  const batchDeleteMaterials = async () => {
    try {
      const response = await fetch('/api/v1/cell-materials/batch-delete', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds }),
      });
      if (!response.ok) throw new Error();
      const payload = (await response.json().catch(() => null)) as {
        deleted?: number;
        skipped?: Array<{ id: string; reason: string }>;
      } | null;
      setSelectedIds([]);
      await actionRef.current?.reload();
      await messageApi.success(
        t('materials.batchDeleted', {
          count: payload?.deleted ?? 0,
          skipped: payload?.skipped?.length ?? 0,
        }),
      );
    } catch {
      await messageApi.error(t('materials.batchDeleteFailed'));
    }
  };

  const columns: ProColumns<CellMaterial>[] = [
    {
      title: t('samples.searchField'),
      dataIndex: 'query',
      hideInTable: true,
      fieldProps: { allowClear: true, placeholder: t('materials.searchHint') },
    },
    {
      title: t('samples.cellLine'),
      dataIndex: 'cellLineId',
      valueType: 'select',
      valueEnum: Object.fromEntries(
        lineOptions.map((option) => [option.value, { text: option.label }]),
      ),
      width: 180,
    },
    {
      title: t('samples.materialName'),
      dataIndex: 'name',
      width: 230,
      ellipsis: true,
    },
    {
      title: t('samples.materialType'),
      dataIndex: 'type',
      valueType: 'select',
      valueEnum: Object.fromEntries(
        materialTypeOptions.map((option) => [
          option.value,
          { text: option.label },
        ]),
      ),
      width: 160,
      render: (_, row) => t(`samples.materialTypeOptions.${row.type}`),
    },
    {
      title: t('samples.validationStatus'),
      dataIndex: 'validationStatus',
      valueType: 'select',
      valueEnum: Object.fromEntries(
        validationStatusOptions.map((option) => [
          option.value,
          { text: option.label },
        ]),
      ),
      width: 130,
      render: (_, row) =>
        t(`samples.validationStatusOptions.${row.validationStatus}`),
    },
    {
      title: t('samples.materialStatus'),
      dataIndex: 'status',
      valueType: 'select',
      valueEnum: Object.fromEntries(
        statusOptions.map((option) => [option.value, { text: option.label }]),
      ),
      initialValue: 'ALL',
      width: 100,
      render: (_, row) => (
        <Tag color={row.status === 'ACTIVE' ? 'green' : 'default'}>
          {t(`samples.materialStatusOptions.${row.status}`)}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      dataIndex: 'actions',
      search: false,
      fixed: 'right',
      width: 180,
      render: (_, row) => (
        <Space className="materials-action-space" size={0}>
          <Button
            type="link"
            disabled={busyId === row.id}
            onClick={() => openEdit(row)}
          >
            {t('common.edit')}
          </Button>
          <Button
            type="link"
            loading={busyId === row.id}
            onClick={() => void updateStatus(row)}
          >
            {row.status === 'ACTIVE' ? t('common.disable') : t('common.enable')}
          </Button>
          <Popconfirm
            title={t('samples.materialDeleteConfirm')}
            description={t('samples.materialDeleteHint')}
            okText={t('common.confirm')}
            cancelText={t('common.cancel')}
            onConfirm={() => void deleteMaterial(row)}
          >
            <Button
              danger
              type="link"
              disabled={busyId === row.id}
            >
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <section>
      {holder}
      <ProTable<CellMaterial>
        rowKey="id"
        actionRef={actionRef}
        formRef={formRef}
        columns={columns}
        request={requestMaterials}
        headerTitle={t('materials.title')}
        toolBarRender={() => [
          <Button
            key="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
          >
            {t('materials.create')}
          </Button>,
        ]}
        search={{ labelWidth: 'auto', defaultCollapsed: true }}
        options={{
          reload: true,
          density: true,
          setting: true,
          fullScreen: true,
        }}
        cardBordered
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        tableLayout="fixed"
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys.map(String)),
        }}
        tableAlertOptionRender={(props) =>
          selectedIds.length > 0 ? (
            <Space size="small">
              <Popconfirm
                title={t('materials.batchDeleteConfirm', {
                  count: selectedIds.length,
                })}
                onConfirm={() => void batchDeleteMaterials()}
              >
                <Button type="link" danger icon={<DeleteOutlined />}>
                  {t('materials.batchDelete')}
                </Button>
              </Popconfirm>
              <Button type="link" onClick={props.onCleanSelected}>
                {t('common.clearSelection')}
              </Button>
            </Space>
          ) : null
        }
      />
      <Modal
        open={open}
        title={editing ? t('samples.editMaterial') : t('materials.create')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        onCancel={() => {
          setOpen(false);
          setEditing(undefined);
          form.resetFields();
        }}
        onOk={() => void form.submit()}
        width={760}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => void save(values)}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="cellLineId"
                label={t('samples.cellLine')}
                rules={[{ required: true }]}
              >
                <Select
                  options={lineOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="type"
                label={helpLabel(
                  t('samples.materialType'),
                  t('samples.materialTooltips.materialType'),
                )}
                rules={[{ required: true }]}
              >
                <Select options={materialTypeOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label={helpLabel(
                  t('samples.materialName'),
                  t('samples.materialTooltips.materialName'),
                )}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="parentMaterialId"
                label={helpLabel(
                  t('samples.parentMaterial'),
                  t('samples.materialTooltips.parentMaterial'),
                )}
              >
                <Select allowClear options={parentOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="modificationType"
                label={t('samples.modificationType')}
              >
                <Select allowClear options={modificationTypeOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="targetGene"
                label={helpLabel(
                  t('samples.targetGene'),
                  t('samples.materialTooltips.targetGene'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            {selectedModificationType === 'TAG' ? (
              <>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="tagName"
                    label={helpLabel(
                      t('samples.tagName'),
                      t('samples.materialTooltips.tagName'),
                    )}
                    preserve={false}
                  >
                    <Input placeholder="HiBiT" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="tagPosition"
                    label={helpLabel(
                      t('samples.tagPosition'),
                      t('samples.materialTooltips.tagPosition'),
                    )}
                    preserve={false}
                  >
                    <Select allowClear options={tagPositionOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="modificationStrategy"
                    label={helpLabel(
                      t('samples.modificationStrategy'),
                      t('samples.materialTooltips.modificationStrategy'),
                    )}
                    preserve={false}
                  >
                    <Select allowClear options={modificationStrategyOptions} />
                  </Form.Item>
                </Col>
              </>
            ) : null}
            <Col xs={24} md={12}>
              <Form.Item
                name="constructName"
                label={helpLabel(
                  t('samples.constructName'),
                  t('samples.materialTooltips.constructName'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="selectionMarker"
                label={helpLabel(
                  t('samples.selectionMarker'),
                  t('samples.materialTooltips.selectionMarker'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="cloneNo"
                label={helpLabel(
                  t('samples.cloneNo'),
                  t('samples.materialTooltips.cloneNo'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="validationStatus"
                label={helpLabel(
                  t('samples.validationStatus'),
                  t('samples.materialTooltips.validationStatus'),
                )}
                rules={[{ required: true }]}
              >
                <Select options={validationStatusOptions} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="notes" label={t('samples.notes')}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </section>
  );
}
